import React from 'react';
import { aciToHex } from '../../utils/dxf';

/**
 * Small floating panel that describes the floor-plan entity currently under the
 * cursor: its source type, color, owning layer and basic geometry. Positioned
 * in screen space next to the pointer, flipping sides near the viewport edges so
 * it never spills outside the canvas.
 */
interface Props {
  /** Pointer position in container (screen) space. */
  x: number;
  y: number;
  containerWidth: number;
  containerHeight: number;
  type: string;
  entityColor: number;
  layerName: string;
  layerColor: number;
  category?: string;
  subcategory?: string;
  layerVisible: boolean;
  frozen?: boolean;
  locked?: boolean;
  pointCount: number;
  /** Geometry size in canvas units. */
  width: number;
  height: number;
  /** Raw DXF units per canvas unit (e.g. mm/unit), used for real-world size. */
  scaleFactor: number;
  /** Bbox center in canvas units. */
  coordX?: number;
  coordY?: number;
  /** First vertex in canvas units. */
  startX?: number;
  startY?: number;
  /** Last vertex in canvas units. */
  endX?: number;
  endY?: number;
}

/** Formats a length given in raw DXF units (assumed mm) as mm or m. */
function formatRealSizeFromDxf(units: number): string {
  if (!Number.isFinite(units)) return '—';
  if (units >= 1000) return `${(units / 1000).toFixed(2)} m`;
  return `${Math.round(units)} mm`;
}

/** Formats an absolute coordinate in raw DXF units (assumed mm). */
function formatCoordFromDxf(units: number): string {
  if (!Number.isFinite(units)) return '—';
  if (Math.abs(units) >= 1000) return `${(units / 1000).toFixed(3)} m`;
  return `${units.toFixed(2)} mm`;
}

/** Human-readable label for an AutoCAD Color Index. */
function aciLabel(aci: number): string {
  if (aci === 256) return 'ByLayer (256)';
  if (aci === 0) return 'ByBlock (0)';
  if (aci === 7) return 'White (7)';
  return `ACI ${aci}`;
}

const TOOLTIP_W = 260;
const OFFSET = 16;

const Swatch: React.FC<{ aci: number; fallback: string }> = ({ aci, fallback }) => (
  <span
    className="inline-block w-3 h-3 rounded-sm border border-black/20 dark:border-white/20 shrink-0 align-middle"
    style={{ background: aciToHex(aci) ?? fallback }}
  />
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start gap-2 leading-tight">
    <span className="text-gray-400 dark:text-gray-500 shrink-0 w-14">{label}</span>
    <span className="text-gray-800 dark:text-gray-100 flex-1 min-w-0 break-words">{children}</span>
  </div>
);

const FloorPlanTooltip: React.FC<Props> = ({
  x,
  y,
  containerWidth,
  containerHeight,
  type,
  entityColor,
  layerName,
  layerColor,
  category,
  subcategory,
  layerVisible,
  frozen,
  locked,
  pointCount,
  width,
  height,
  scaleFactor,
  coordX,
  coordY,
  startX,
  startY,
  endX,
  endY,
}) => {
  // Flip to the left of the cursor if there isn't room on the right.
  const left =
    x + OFFSET + TOOLTIP_W > containerWidth ? Math.max(8, x - OFFSET - TOOLTIP_W) : x + OFFSET;
  // Keep the panel within the vertical bounds (rough height estimate).
  const top = Math.min(Math.max(8, y + OFFSET), Math.max(8, containerHeight - 240));

  const stateFlags = [
    !layerVisible && 'hidden',
    frozen && 'frozen',
    locked && 'locked',
  ].filter(Boolean) as string[];

  const hasCenter = typeof coordX === 'number' && typeof coordY === 'number';
  const hasStart = typeof startX === 'number' && typeof startY === 'number';
  const hasEnd = typeof endX === 'number' && typeof endY === 'number';
  const showEndpoints = pointCount === 2 && hasStart && hasEnd;

  return (
    <div
      className="pointer-events-none absolute z-20 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 shadow-xl backdrop-blur-sm px-3 py-2 text-[11px] space-y-1"
      style={{ left, top, width: TOOLTIP_W }}
    >
      <div className="flex items-center gap-2 pb-1 mb-1 border-b border-gray-100 dark:border-gray-800">
        <Swatch aci={entityColor === 256 ? layerColor : entityColor} fallback="#64748b" />
        <span className="font-semibold text-gray-900 dark:text-gray-100">{type}</span>
        <span className="ml-auto text-[10px] text-gray-400">{pointCount} pts</span>
      </div>

      <Row label="Layer">
        <span className="font-mono break-all">{layerName}</span>
      </Row>
      <Row label="Category">
        <span className="px-1 rounded bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300">
          {category || '—'}
        </span>
      </Row>
      <Row label="Sub-category">
        <span className="px-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
          {subcategory || '—'}
        </span>
      </Row>
      <Row label="Color">
        <Swatch aci={entityColor} fallback="#64748b" /> <span>{aciLabel(entityColor)}</span>
      </Row>
      <Row label="Layer clr">
        <Swatch aci={layerColor} fallback="#64748b" /> <span>{aciLabel(layerColor)}</span>
      </Row>
      {hasCenter && (
        <Row label="Coords">
          X {formatCoordFromDxf(coordX! * scaleFactor)} · Y{' '}
          {formatCoordFromDxf(coordY! * scaleFactor)}
        </Row>
      )}
      {showEndpoints && (
        <>
          <Row label="Start">
            X {formatCoordFromDxf(startX! * scaleFactor)} · Y{' '}
            {formatCoordFromDxf(startY! * scaleFactor)}
          </Row>
          <Row label="End">
            X {formatCoordFromDxf(endX! * scaleFactor)} · Y{' '}
            {formatCoordFromDxf(endY! * scaleFactor)}
          </Row>
        </>
      )}
      <Row label="Size">
        {formatRealSizeFromDxf(width * scaleFactor)} × {formatRealSizeFromDxf(height * scaleFactor)}
      </Row>
      {stateFlags.length > 0 && (
        <Row label="State">
          <span className="text-amber-600 dark:text-amber-400">{stateFlags.join(', ')}</span>
        </Row>
      )}
    </div>
  );
};

export default FloorPlanTooltip;
